import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Row, Col, Alert, Badge, Spinner, Tabs, Tab } from 'react-bootstrap';
import { User, Mail, Shield, Key, Save, Activity } from 'lucide-react';
import { getProfile, updateProfile, changePassword } from '../services/profile';

const Profile = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [toastShow, setToastShow] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    notifications_enabled: true,
    two_factor_enabled: false
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Fetch profile
  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await getProfile();
      setProfile(response.data);
      setFormData({
        username: response.data.username,
        email: response.data.email,
        notifications_enabled: response.data.notifications_enabled,
        two_factor_enabled: response.data.two_factor_enabled
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      showToast('Failed to load profile: ' + (error.message || error), 'danger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  // Show toast notification
  const showToast = (message, variant = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setToastShow(true);
  };

  const handleInputChange = (field, value) => {
    // Empêcher la modification de l'email
    if (field === 'email') return;
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Ne pas envoyer l'email dans les données de mise à jour
      const { email, ...updateData } = formData;
      await updateProfile(updateData);
      showToast('Profile updated successfully');
      fetchProfile(); // Refresh profile data
    } catch (error) {
      console.error('Failed to update profile:', error);
      showToast('Failed to update profile: ' + (error.message || error), 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      showToast('New passwords do not match', 'danger');
      return;
    }

    setChangingPassword(true);

    try {
      await changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      showToast('Password changed successfully');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error('Failed to change password:', error);
      showToast('Failed to change password: ' + (error.message || error), 'danger');
    } finally {
      setChangingPassword(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading profile...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Toast Notifications */}
      {toastShow && (
        <Alert variant={toastVariant} className="mb-4" onClose={() => setToastShow(false)} dismissible>
          {toastMessage}
        </Alert>
      )}

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="h2 fw-bold mb-1">User Profile</h1>
          <p className="text-muted">Manage your account settings and preferences</p>
        </div>
        <Badge bg="primary" className="fs-6 px-3 py-2">
          {profile?.role}
        </Badge>
      </div>

      {/* Profile Tabs */}
      <Card className="shadow-sm border-0">
        <Card.Body className="p-0">
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="px-3 pt-3"
            fill
          >
            {/* Profile Tab */}
            <Tab eventKey="profile" title={
              <span className="d-flex align-items-center">
                <User size={18} className="me-2" />
                Profile
              </span>
            }>
              <div className="p-4">
                <Form onSubmit={handleProfileSave}>
                  <Row className="g-4">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-medium">
                          <User size={16} className="me-2" />
                          Username
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.username}
                          onChange={(e) => handleInputChange('username', e.target.value)}
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-medium">
                          <Mail size={16} className="me-2" />
                          Email Address
                        </Form.Label>
                        <Form.Control
                          type="email"
                          value={formData.email}
                          readOnly
                          className="bg-light"
                        />
                        <Form.Text className="text-muted">
                          Email address cannot be changed
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-medium">
                          <Shield size={16} className="me-2" />
                          Two-Factor Authentication
                        </Form.Label>
                        <Form.Select
                          value={formData.two_factor_enabled}
                          onChange={(e) => handleInputChange('two_factor_enabled', e.target.value === 'true')}
                        >
                          <option value={false}>Disabled</option>
                          <option value={true}>Enabled</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-medium">
                          <Activity size={16} className="me-2" />
                          Notifications
                        </Form.Label>
                        <Form.Select
                          value={formData.notifications_enabled}
                          onChange={(e) => handleInputChange('notifications_enabled', e.target.value === 'true')}
                        >
                          <option value={true}>Enabled</option>
                          <option value={false}>Disabled</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col xs={12}>
                      <div className="d-flex justify-content-end">
                        <Button 
                          variant="primary" 
                          type="submit"
                          disabled={saving}
                        >
                          {saving ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save size={18} className="me-2" />
                              Save Changes
                            </>
                          )}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </div>
            </Tab>

            {/* Security Tab */}
            <Tab eventKey="security" title={
              <span className="d-flex align-items-center">
                <Key size={18} className="me-2" />
                Security
              </span>
            }>
              <div className="p-4">
                <Form onSubmit={handlePasswordSave}>
                  <Row className="g-4">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-medium">Current Password</Form.Label>
                        <Form.Control
                          type="password"
                          value={passwordData.current_password}
                          onChange={(e) => handlePasswordChange('current_password', e.target.value)}
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-medium">New Password</Form.Label>
                        <Form.Control
                          type="password"
                          value={passwordData.new_password}
                          onChange={(e) => handlePasswordChange('new_password', e.target.value)}
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-medium">Confirm New Password</Form.Label>
                        <Form.Control
                          type="password"
                          value={passwordData.confirm_password}
                          onChange={(e) => handlePasswordChange('confirm_password', e.target.value)}
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col xs={12}>
                      <Alert variant="info" className="small">
                        <strong>Password requirements:</strong>
                        <ul className="mb-0 mt-2">
                          <li>Minimum 8 characters</li>
                          <li>At least one uppercase letter</li>
                          <li>At least one lowercase letter</li>
                          <li>At least one number</li>
                          <li>At least one special character</li>
                        </ul>
                      </Alert>
                    </Col>

                    <Col xs={12}>
                      <div className="d-flex justify-content-end">
                        <Button 
                          variant="primary" 
                          type="submit"
                          disabled={changingPassword}
                        >
                          {changingPassword ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Changing...
                            </>
                          ) : (
                            <>
                              <Key size={18} className="me-2" />
                              Change Password
                            </>
                          )}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </div>
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>

      {/* Account Info Card */}
      <Row className="mt-4">
        <Col md={12}>
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-white border-0 py-3">
              <h5 className="fw-bold mb-0">Account Information</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="text-muted">Member since</span>
                    <strong>{formatDate(profile?.created_at)}</strong>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="text-muted">Last login</span>
                    <strong>{formatDate(profile?.last_login)}</strong>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-muted">Account status</span>
                    <Badge bg={profile?.is_active ? 'success' : 'secondary'}>
                      {profile?.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Profile;